import React from 'react';
import { createPortal } from 'react-dom';
import { isValid, startOfMonth } from 'date-fns';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { CustomDateCalendar } from './CustomDateCalendar';
import {
  applyTimeToDate,
  DATE_PICKER_MODE,
  formatPickerValue,
  isSameMonth,
  isAfterMaxDate,
  notifyCustomDatePickerOpened,
  parsePickerInput,
  subscribeCustomDatePickerOpened,
  type CustomDatePickerMode,
  type DatePickerRestrictions,
} from './customDatePicker.utils';
import './customDatePicker.css';
import { useDatePickerRestrictions } from './useDatePickerRestrictions';
import { DatePickerMaxDateContext } from './datePickerMaxDate';

export type { CustomDatePickerMode };
export { DATE_PICKER_MODE };

export type CustomDatePickerProps = {
  value: Date | null;
  defaultValue?: Date | null | (() => Date | null);
  onChange: (value: Date | null) => void;
  mode?: CustomDatePickerMode;
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  /** Minimal styling for embedded grid cells */
  compact?: boolean;
  error?: boolean;
  helperText?: string;
  /** Divider for filter row */
  showDividerBeforeIcon?: boolean;
  dateRestrictions?: DatePickerRestrictions;
  maxDate?: Date | null;
  /** Opens the calendar as soon as the picker is mounted. */
  openOnMount?: boolean;
  /** Hides the text input when the picker is used as a calendar-only control. */
  hideInput?: boolean;
  /** Element used to position the calendar when the input is hidden. */
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
    />
  </svg>
);

const ariaLabelForMode = (mode: CustomDatePickerMode, label?: string) => {
  if (label) return label;
  if (mode === DATE_PICKER_MODE.DATETIME) return 'Date and time';
  if (mode === DATE_PICKER_MODE.MONTH) return 'Month';
  return 'Date';
};

const getTodayValue = (mode: CustomDatePickerMode): Date => {
  const now = new Date();
  if (mode === DATE_PICKER_MODE.MONTH) return startOfMonth(now);
  if (mode === DATE_PICKER_MODE.DATE) {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    return today;
  }
  now.setSeconds(0, 0);
  return now;
};

const toDateOnly = (day: Date): Date => {
  const next = new Date(day);
  next.setHours(0, 0, 0, 0);
  return next;
};

export const CustomDatePicker = ({
  value,
  defaultValue = null,
  onChange,
  mode = DATE_PICKER_MODE.DATE,
  disabled = false,
  readOnly = false,
  label,
  compact = false,
  error = false,
  helperText,
  showDividerBeforeIcon = false,
  dateRestrictions,
  maxDate: explicitMaxDate,
  openOnMount = false,
  hideInput = false,
  anchorEl: externalAnchorEl = null,
  onClose,
}: CustomDatePickerProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const popperRef = React.useRef<HTMLDivElement>(null);
  const suppressOpenOnFocusRef = React.useRef(false);
  const selectedRef = React.useRef<Date | null>(value);
  const inputId = React.useId();
  const instanceId = React.useId();

  const [isOpen, setIsOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Date | null>(value);
  const [inputText, setInputText] = React.useState(() => formatPickerValue(value, mode));
  const [inputError, setInputError] = React.useState(false);
  const [viewMonth, setViewMonth] = React.useState(() =>
    value && isValid(value) ? startOfMonth(value) : startOfMonth(new Date())
  );
  const [popperStyle, setPopperStyle] = React.useState<React.CSSProperties>({});
  const restrictions = useDatePickerRestrictions(dateRestrictions);
  const contextMaxDate = React.useContext(DatePickerMaxDateContext);
  const maxDate = explicitMaxDate !== undefined ? explicitMaxDate : contextMaxDate;
  const displayLabel = readOnly ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <LockOutlined sx={{ fontSize: 14 }} aria-hidden="true" />
      {label}
    </span>
  ) : (
    label
  );

  React.useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  React.useEffect(() => {
    if (readOnly && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, readOnly]);

  React.useEffect(() => {
    return subscribeCustomDatePickerOpened((openerId) => {
      if (openerId !== instanceId) {
        setIsOpen(false);
        onClose?.();
      }
    });
  }, [instanceId, onClose]);

  React.useEffect(() => {
    if (isOpen) return;
    setSelected(value);
    setInputText(formatPickerValue(value, mode));
    setInputError(false);
    if (value && isValid(value)) {
      setViewMonth(startOfMonth(value));
    }
  }, [value, mode, isOpen]);

  const updatePopperPosition = React.useCallback(() => {
    const anchor = externalAnchorEl ?? rootRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 20;

    const minPopperWidth = mode === DATE_PICKER_MODE.DATETIME ? 340 : 280;
    const preferredWidth = Math.max(rect.width, minPopperWidth);
    const popperWidth = popperRef.current?.offsetWidth || preferredWidth;
    const popperHeight = popperRef.current?.offsetHeight || 320;

    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openAbove = spaceBelow < popperHeight + 4 && spaceAbove > spaceBelow;

    let top = openAbove ? rect.top - popperHeight - 4 : rect.bottom + 4;
    top = Math.min(top, viewportHeight - popperHeight - margin);
    top = Math.max(margin, top);

    let left = rect.left;
    left = Math.min(left, viewportWidth - popperWidth - margin);
    left = Math.max(margin, left);

    setPopperStyle({
      position: 'fixed',
      left,
      top,
      zIndex: 1400,
      minWidth: preferredWidth,
      maxWidth: viewportWidth - margin * 2,
    });
  }, [externalAnchorEl, mode]);

  React.useEffect(() => {
    if (!isOpen) return;

    updatePopperPosition();
    const rafId = window.requestAnimationFrame(() => updatePopperPosition());
    const onScrollOrResize = () => updatePopperPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);

    const popperEl = popperRef.current;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && popperEl
        ? new ResizeObserver(() => updatePopperPosition())
        : null;
    if (popperEl && resizeObserver) {
      resizeObserver.observe(popperEl);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
      resizeObserver?.disconnect();
    };
  }, [isOpen, updatePopperPosition]);

  const handleCancel = React.useCallback(() => {
    setSelected(value);
    suppressOpenOnFocusRef.current = true;
    setIsOpen(false);
    onClose?.();
  }, [onClose, value]);

  const handleOk = React.useCallback(() => {
    if (readOnly) return;

    const next = selectedRef.current;
    onChange(next);
    setInputText(formatPickerValue(next, mode));
    setInputError(false);
    suppressOpenOnFocusRef.current = true;
    setIsOpen(false);
    onClose?.();
  }, [mode, onChange, onClose, readOnly]);

  const handleToday = React.useCallback(() => {
    if (readOnly) return;

    const today = getTodayValue(mode);
    if (mode !== DATE_PICKER_MODE.MONTH && restrictions?.isDisabled(today)) return;
    setSelected(today);
    setViewMonth(startOfMonth(today));
  }, [mode, readOnly, restrictions]);

  React.useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popperRef.current?.contains(target)) {
        return;
      }
      handleCancel();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleCancel]);

  const commitInput = () => {
    if (readOnly) return;

    if (!inputText.trim()) {
      setInputError(false);
      onChange(null);
      setSelected(null);
      return;
    }

    const parsed = parsePickerInput(inputText, mode);
    if (
      !parsed ||
      (mode !== DATE_PICKER_MODE.MONTH &&
        (restrictions?.isDisabled(parsed) || isAfterMaxDate(parsed, maxDate ?? undefined)))
    ) {
      setInputError(true);
      setInputText(formatPickerValue(value, mode));
      return;
    }

    setInputError(false);
    setSelected(parsed);
    onChange(parsed);
  };

  const handleSelectDay = (day: Date) => {
    if (readOnly) return;

    const next = mode === DATE_PICKER_MODE.DATETIME ? day : toDateOnly(day);
    setSelected(next);
    setViewMonth((current) => (isSameMonth(current, next) ? current : startOfMonth(next)));
  };

  const handleSelectMonth = (month: Date) => {
    if (readOnly) return;

    const next = startOfMonth(month);
    setSelected(next);
    setViewMonth(next);
  };

  const handleTimeChange = (hours: number, minutes: number) => {
    if (readOnly) return;

    setSelected((current) => {
      const base = current && isValid(current) ? current : new Date();
      const next = applyTimeToDate(base, hours, minutes);
      selectedRef.current = next;
      return next;
    });
  };

  const openCalendar = React.useCallback(() => {
    if (disabled || readOnly || isOpen) return;

    const resolvedDefault = typeof defaultValue === 'function' ? defaultValue() : defaultValue;

    const initial =
      value && isValid(value)
        ? value
        : resolvedDefault && isValid(resolvedDefault)
          ? resolvedDefault
          : getTodayValue(mode);

    setSelected(initial);
    setViewMonth(startOfMonth(initial));
    notifyCustomDatePickerOpened(instanceId);
    setIsOpen(true);
  }, [defaultValue, disabled, instanceId, isOpen, mode, readOnly, value]);

  React.useEffect(() => {
    if (openOnMount) {
      openCalendar();
    }
  }, [openCalendar, openOnMount]);

  const handleInputFocus = () => {
    if (suppressOpenOnFocusRef.current) {
      suppressOpenOnFocusRef.current = false;
      return;
    }
    openCalendar();
  };

  const handleInputClick = () => {
    if (isOpen) return;
    suppressOpenOnFocusRef.current = false;
    openCalendar();
  };

  const popper =
    isOpen &&
    createPortal(
      <div
        ref={popperRef}
        className="custom-date-picker-popper"
        style={popperStyle}
        role="presentation"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="custom-date-picker-paper">
          <CustomDateCalendar
            viewMonth={viewMonth}
            selectedDate={selected}
            mode={mode}
            onViewMonthChange={setViewMonth}
            onSelectDay={handleSelectDay}
            onSelectMonth={handleSelectMonth}
            onTimeChange={handleTimeChange}
            dateRestrictions={dateRestrictions}
            maxDate={maxDate ?? undefined}
          />
          <div className="custom-date-picker-actions">
            <button
              type="button"
              className="custom-date-picker-actions__btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleToday}
            >
              Today
            </button>
            <div className="custom-date-picker-actions__right">
              <button
                type="button"
                className="custom-date-picker-actions__btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="custom-date-picker-actions__btn custom-date-picker-actions__btn--primary"
                onClick={handleOk}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {!hideInput && (
        <TextField
          ref={rootRef}
          id={inputId}
          fullWidth
          error={error || inputError}
          disabled={disabled}
          label={!compact ? displayLabel : undefined}
          InputLabelProps={{ shrink: true }}
          value={inputText}
          onChange={(event) => {
            if (readOnly) return;
            setInputText(event.target.value);
            setInputError(false);
          }}
          onBlur={() => {
            if (!isOpen) commitInput();
          }}
          onKeyDown={(event) => {
            if (readOnly) return;
            if (event.key === 'Enter') {
              event.preventDefault();
              commitInput();
            }
            if (event.key === 'ArrowDown' && !isOpen) {
              event.preventDefault();
              suppressOpenOnFocusRef.current = false;
              openCalendar();
            }
          }}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          helperText={
            !compact && (error || inputError)
              ? inputError
                ? 'Invalid date format'
                : (helperText ?? 'Invalid value')
              : !compact
                ? helperText
                : undefined
          }
          FormHelperTextProps={{
            sx: { ml: 0, mt: 0.5 },
          }}
          inputProps={{
            'aria-label': ariaLabelForMode(mode, label),
            readOnly,
            'aria-readonly': readOnly,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {showDividerBeforeIcon && (
                  <div
                    className="custom-date-picker__divider"
                    aria-hidden="true"
                    role="presentation"
                  />
                )}
                <IconButton
                  size="small"
                  disabled={disabled || readOnly}
                  aria-label="Open calendar"
                  edge="end"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (readOnly) return;
                    if (isOpen) {
                      handleCancel();
                      return;
                    }
                    suppressOpenOnFocusRef.current = false;
                    openCalendar();
                  }}
                  sx={{
                    color: isOpen ? 'primary.main' : 'action.active',
                    padding: '4px',
                  }}
                >
                  <CalendarIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            ...(hideInput
              ? {
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                }
              : {}),
            ...(compact
              ? {
                  margin: 0,
                  height: '100%',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiInputBase-root': {
                    padding: 0,
                    paddingRight: '2px',
                    height: '100%',
                    background: 'transparent',
                  },
                  '& .MuiInputBase-input': {
                    padding: '0 0 0 4px',
                    height: '100%',
                    fontSize: '0.8125rem',
                    fontFamily: 'inherit',
                    fontVariantNumeric: 'normal',
                  },
                  '& .MuiInputAdornment-root': {
                    height: '100%',
                    maxHeight: 'none',
                    margin: 0,
                  },
                }
              : {}),
            ...(readOnly
              ? {
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                }
              : {}),
          }}
        />
      )}
      {popper}
    </>
  );
};
